/**
 * Watchpick v1 landing page behaviour.
 *
 * <tws-wpick-hero>   gallery + review carousels (native scroll-snap; the
 *                    buttons only scroll the track) and the desktop tabs.
 * <tws-wpick-sticky> mirrors the hero's checked `wpick-plan` radio, switches
 *                    it from its own pills, opens the plan sheet on phones,
 *                    and hides while the hero CTA is on screen.
 * <tws-wpick-winners> mirrors a sideways scroller (winners row, winners wall,
 *                    value cards) onto its progress bar or dots, and lets a
 *                    mouse grab and drag it.
 * <tws-wpick-gifts>  the pick-a-card pre-lander: each flip reveals the next
 *                    gift, the last one swaps in the claim button.
 */
(function () {
  if (window.twsWpickReady) return;
  window.twsWpickReady = true;

  const PLAN_GROUP = "wpick-plan";

  function initCarousel(root) {
    const track = root.querySelector("[data-carousel-track]");
    if (!track) return;
    const slides = Array.from(track.children);
    const targets = Array.from(root.querySelectorAll("[data-carousel-to]"));
    const dots = Array.from(root.querySelectorAll(".tws-wpick-hero__dots > *"));
    const thumbs = root.querySelector(".tws-wpick-hero__thumbs");

    const indexNow = () => {
      let best = 0;
      slides.forEach((slide, i) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft))
          best = i;
      });
      return best;
    };

    const go = (i) => {
      const n = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[n].offsetLeft, behavior: "smooth" });
    };

    const mark = () => {
      const i = indexNow();
      dots.forEach((dot, n) => dot.classList.toggle("is-active", n === i));
      targets.forEach((t) => {
        if (t.matches(".tws-wpick-hero__thumb"))
          t.setAttribute("aria-current", String(Number(t.dataset.carouselTo) === i));
      });
      const active = thumbs && thumbs.children[i];
      if (active)
        thumbs.scrollTo({
          left: active.offsetLeft - thumbs.clientWidth / 2 + active.clientWidth / 2,
          behavior: "smooth",
        });
    };

    let frame;
    track.addEventListener("scroll", () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(mark);
    });
    targets.forEach((t) => t.addEventListener("click", () => go(Number(t.dataset.carouselTo))));
    root.querySelector("[data-carousel-prev]")?.addEventListener("click", () => go(indexNow() - 1));
    root.querySelector("[data-carousel-next]")?.addEventListener("click", () => go(indexNow() + 1));
  }

  class TwsWpickHero extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll("[data-carousel]").forEach(initCarousel);

      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      tabs.forEach((tab) =>
        tab.addEventListener("click", () => {
          tabs.forEach((t) => {
            const on = t === tab;
            t.setAttribute("aria-selected", String(on));
            const panel = document.getElementById(t.getAttribute("aria-controls"));
            if (panel) panel.hidden = !on;
          });
        }),
      );

      // Each plan can carry its own CTA label. On the document: the sticky sheet borrows the cards.
      const ctaLabel = this.querySelector("[data-wpick-cta-label]");
      if (ctaLabel)
        document.addEventListener("change", (event) => {
          if (event.target.name === PLAN_GROUP && event.target.dataset.ctaLabel)
            ctaLabel.textContent = event.target.dataset.ctaLabel;
        });

      // A plan with its own gallery image swaps it into the slide it picked (and
      // that slide's thumb) while checked. The gallery stays where it is. Also run
      // on load: a reload can restore a different radio than the server checked.
      const planSlides = Array.from(this.querySelectorAll("[data-plan-slide]"));
      const showPlanMedia = (plan) =>
        planSlides.forEach((slot) => {
          const media = Array.from(slot.querySelectorAll(":scope > [data-plan-media]"));
          const match = media.find((m) => m.dataset.planMedia === plan);
          media.forEach((m) => (m.hidden = m !== (match || media.find((d) => d.dataset.planMedia === "default"))));
        });
      if (planSlides.length) {
        const checked = Array.from(document.getElementsByName(PLAN_GROUP)).find((r) => r.checked);
        if (checked) showPlanMedia(checked.dataset.plan);
        document.addEventListener("change", (event) => {
          if (event.target.name === PLAN_GROUP) showPlanMedia(event.target.dataset.plan);
        });
      }

      // Welcome kit upsell ("Switch to Annual"). Delegated on the document because
      // the sticky bar's sheet borrows the plan cards.
      if (!TwsWpickHero.switching) {
        TwsWpickHero.switching = true;
        document.addEventListener("click", (event) => {
          const button = event.target.closest("[data-plan-switch]");
          if (!button) return;
          event.preventDefault();
          const radio = Array.from(document.getElementsByName(PLAN_GROUP)).find(
            (r) => r.dataset.plan === button.dataset.planSwitch,
          );
          if (!radio) return;
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
          radio.closest("label")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
      }
    }
  }

  class TwsWpickSticky extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(document.getElementsByName(PLAN_GROUP));
      this.pills = this.querySelector("[data-sticky-pills]");
      this.sheet = this.querySelector("dialog");

      if (this.radios.length) {
        this.classList.add("has-plans");
        this.radios.forEach((radio) => {
          const pill = document.createElement("button");
          pill.type = "button";
          pill.textContent = radio.dataset.planName || radio.value;
          pill.addEventListener("click", () => this.pick(radio));
          this.pills?.append(pill);
          radio.addEventListener("change", () => this.sync());
        });
        this.sync();
      }

      this.querySelector("[data-sticky-open]")?.addEventListener("click", () => this.openSheet());
      this.sheet?.addEventListener("close", () => this.closeSheet());
      this.sheet?.addEventListener("click", (event) => {
        if (event.target === this.sheet || event.target.closest("[data-sheet-close]")) this.sheet.close();
      });

      this.watch();
    }

    pick(radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }

    sync() {
      const radio = this.radios.find((r) => r.checked) || this.radios[0];
      if (!radio) return;
      const d = radio.dataset;
      const set = (key, value) =>
        this.querySelectorAll(`[data-sticky="${key}"]`).forEach((el) => (el.textContent = value || ""));
      set("name", d.planName);
      set("price", d.ctaPrice);
      set("was", d.ctaWas);
      set("interval", d.interval);
      set("entries", d.entries);
      set("entries-text", d.entriesText ? " " + d.entriesText : "");
      Array.from(this.pills?.children || []).forEach((pill, i) =>
        pill.setAttribute("aria-pressed", String(this.radios[i] === radio)),
      );
    }

    /* The sheet borrows the hero's plan cards so there is only ever one radio group. */
    openSheet() {
      const plans = document.querySelector("[data-wpick-plans]");
      const slot = this.sheet?.querySelector("[data-sheet-plans]");
      if (!plans || !slot) {
        document.getElementById("membership-plans")?.scrollIntoView({ behavior: "smooth" });
        return;
      }
      this.placeholder = document.createComment("wpick-plans");
      plans.before(this.placeholder);
      slot.append(plans);
      this.sheet.showModal();
    }

    closeSheet() {
      const plans = this.sheet?.querySelector("[data-wpick-plans]");
      if (plans && this.placeholder) {
        this.placeholder.replaceWith(plans);
        this.placeholder = null;
      }
    }

    /* Hidden while any [data-wpick-sticky-hide] CTA (the hero's, a banner's) is
       on screen. Before the last one (still below the viewport) the bar is
       `is-before`: phones show a plain link to the plans,
       desktop shows the full plan + price bar, as it does past the CTA.
       Desktop CSS also hides it while `is-top` (page scrolled under ~100px). */
    /* A scroll listener rather than IntersectionObserver: a jump straight past
       the CTA (anchor link) never intersects, so the observer would not fire.
       Capture phase also catches .page-wrapper, the scroller on desktop. */
    watch() {
      const targets = Array.from(document.querySelectorAll("[data-wpick-sticky-hide]"));
      const wrapper = document.querySelector(".page-wrapper");
      let frame;
      const update = () => {
        const rects = targets.map((t) => t.getBoundingClientRect());
        const onScreen = rects.some((r) => r.bottom > 0 && r.top < window.innerHeight);
        // Several CTAs (e.g. a banner above the hero): "before" while one is still ahead.
        const before = !onScreen && rects.some((r) => r.top >= window.innerHeight);
        const scrolled = Math.max(window.scrollY, wrapper?.scrollTop || 0);
        this.classList.toggle("is-before", before);
        this.classList.toggle("is-visible", !onScreen);
        this.classList.toggle("is-top", scrolled < 100);
      };
      document.addEventListener(
        "scroll",
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(update);
        },
        { capture: true, passive: true },
      );
      update();
    }
  }

  class TwsWpickWinners extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-wpick-track]");
      this.thumb = this.querySelector("[data-wpick-thumb]");
      this.dots = this.querySelectorAll("[data-wpick-dot]");
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.track.addEventListener("scroll", this.update, { passive: true });
      new ResizeObserver(this.update).observe(this.track);
      this.initDrag();
      // Optional arrows step one card (card width + gap) either way.
      const step = (dir) => {
        const card = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: dir * (card.offsetWidth + gap), behavior: "smooth" });
      };
      this.querySelector("[data-wpick-prev]")?.addEventListener("click", () => step(-1));
      this.querySelector("[data-wpick-next]")?.addEventListener("click", () => step(1));
    }

    // Mouse grab-and-drag; touch and trackpads already scroll natively. The
    // drag only starts past a few pixels so the cards' links stay clickable,
    // and the click that ends a drag is swallowed.
    initDrag() {
      const track = this.track;
      let startX = 0;
      let startLeft = 0;
      let pointer = null;
      let dragged = false;

      track.addEventListener("dragstart", (event) => event.preventDefault());
      track.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        if (!this.classList.contains("is-scrollable")) return;
        pointer = event.pointerId;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        dragged = false;
      });
      track.addEventListener("pointermove", (event) => {
        if (event.pointerId !== pointer) return;
        const dx = event.clientX - startX;
        if (!dragged && Math.abs(dx) < 6) return;
        if (!dragged) {
          dragged = true;
          track.setPointerCapture(pointer);
          this.classList.add("is-dragging");
        }
        track.scrollLeft = startLeft - dx;
      });
      const end = (event) => {
        if (event.pointerId !== pointer) return;
        pointer = null;
        // Restoring scroll-snap lets the browser settle on the nearest card.
        this.classList.remove("is-dragging");
      };
      track.addEventListener("pointerup", end);
      track.addEventListener("pointercancel", end);
      track.addEventListener(
        "click",
        (event) => {
          if (!dragged) return;
          dragged = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    // A thumb takes the viewport's share of the track and travels with the
    // scroll; dots light up the one nearest the scroll position.
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const ratio = Math.min(clientWidth / scrollWidth, 1);
      const progress = ratio < 1 ? scrollLeft / (scrollWidth - clientWidth) : 0;
      this.classList.toggle("is-scrollable", ratio < 1);
      if (this.thumb) {
        this.thumb.style.width = `${ratio * 100}%`;
        this.thumb.style.transform = `translateX(${progress * (1 / ratio - 1) * 100}%)`;
      }
      const active = Math.round(progress * (this.dots.length - 1));
      this.dots.forEach((dot, i) => dot.classList.toggle("is-active", i === active));
    }
  }

  const CONFETTI = ["#ffc953", "#fff3d6", "#ffe08a", "#d4a843", "#1f8077"];
  const NOTES = [523.25, 659.25, 783.99, 1046.5, 1318.5]; // C5 E5 G5 C6 E6
  const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Synthesised sound effects (no audio files). Only ever called from a tap,
  // so browsers allow the AudioContext to start.
  let audio;
  function playSound(kind, step) {
    try {
      audio = audio || new (window.AudioContext || window.webkitAudioContext)();
      if (audio.state === "suspended") audio.resume();
    } catch (e) {
      return;
    }
    const start = audio.currentTime;
    const out = audio.createGain();
    out.gain.value = 0.5;
    out.connect(audio.destination);

    const tone = (freq, at, dur, type, vol, toFreq) => {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      const t = start + at;
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t);
      if (toFreq) osc.frequency.exponentialRampToValueAtTime(toFreq, t + dur);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(vol, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(gain).connect(out);
      osc.start(t);
      osc.stop(t + dur + 0.05);
    };

    const hiss = (at, dur, vol, filterType, freq) => {
      const length = Math.floor(audio.sampleRate * dur);
      const buffer = audio.createBuffer(1, length, audio.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 2;
      const src = audio.createBufferSource();
      const filter = audio.createBiquadFilter();
      const gain = audio.createGain();
      src.buffer = buffer;
      filter.type = filterType;
      filter.frequency.value = freq;
      gain.gain.value = vol;
      src.connect(filter).connect(gain).connect(out);
      src.start(start + at);
    };

    if (kind === "flip") {
      // A card whoosh, then a note that climbs with every gift found.
      const note = NOTES[step % NOTES.length];
      hiss(0, 0.18, 0.35, "bandpass", 2200);
      tone(note, 0.14, 0.32, "triangle", 0.22);
      tone(note * 2, 0.14, 0.2, "sine", 0.06);
    } else {
      // The blast: a thump and a pop, a rising fanfare, then sparkles.
      tone(110, 0, 0.3, "sine", 0.55, 40);
      hiss(0, 0.4, 0.7, "lowpass", 1400);
      NOTES.forEach((f, i) => tone(f, 0.1 + i * 0.07, 0.45, "triangle", 0.18));
      tone(NOTES[4], 0.45, 0.9, "sine", 0.12);
      for (let i = 0; i < 12; i++) tone(2200 + Math.random() * 2600, 0.35 + Math.random() * 1, 0.14, "sine", 0.04);
    }
  }

  // Confetti blast from `point` (viewport coordinates) across the whole screen, with two side
  // cannons a beat later; pieces flutter down and the canvas removes itself.
  function burstConfetti(host, point) {
    const canvas = document.createElement("canvas");
    canvas.className = "tws-wpick-gifts__confetti";
    host.append(canvas);
    const box = canvas.getBoundingClientRect();
    const origin = { x: point.x - box.left, y: point.y - box.top };
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const rand = (min, max) => min + Math.random() * (max - min);
    const reach = Math.max(W, H) / 900;
    const bits = [];
    const add = (count, x, y, fromAngle, toAngle, minSpeed, maxSpeed) => {
      for (let i = 0; i < count; i++) {
        const angle = rand(fromAngle, toAngle);
        const speed = (minSpeed + (maxSpeed - minSpeed) * Math.sqrt(Math.random())) * reach;
        bits.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          w: rand(6, 12),
          h: rand(8, 16),
          color: CONFETTI[i % CONFETTI.length],
          rot: rand(0, 6.28),
          spin: rand(-0.2, 0.2),
          tilt: rand(0, 6.28),
          flutter: rand(0.08, 0.2),
          gravity: rand(0.22, 0.42),
          fall: rand(2.2, 4.6),
          round: Math.random() < 0.25,
        });
      }
    };
    const draw = (bit) => {
      ctx.save();
      ctx.translate(bit.x, bit.y);
      ctx.rotate(bit.rot);
      ctx.scale(1, Math.cos(bit.tilt));
      ctx.fillStyle = bit.color;
      if (bit.round) {
        ctx.beginPath();
        ctx.arc(0, 0, bit.w / 2, 0, 6.28);
        ctx.fill();
      } else {
        ctx.fillRect(-bit.w / 2, -bit.h / 2, bit.w, bit.h);
      }
      ctx.restore();
    };

    if (reducedMotion()) {
      add(70, 0, 0, 0, 0, 0, 0);
      bits.forEach((bit) => {
        bit.x = rand(0, W);
        bit.y = rand(0, H);
        draw(bit);
      });
      return;
    }

    add(150, origin.x, origin.y, 0, Math.PI * 2, 6, 20);
    setTimeout(() => {
      add(55, 0, H, -1.35, -0.75, 14, 24);
      add(55, W, H, -2.4, -1.8, 14, 24);
    }, 280);

    const born = performance.now();
    let last = born;
    const tick = (now) => {
      const dt = Math.min((now - last) / 16.7, 3);
      const age = now - born;
      last = now;
      ctx.clearRect(0, 0, W, H);

      if (age < 450) {
        const fade = 1 - age / 450;
        ctx.beginPath();
        ctx.arc(origin.x, origin.y, 24 + age * 0.9 * reach, 0, 6.28);
        ctx.strokeStyle = `rgb(255 201 83 / ${fade})`;
        ctx.lineWidth = 1 + fade * 8;
        ctx.stroke();
      }

      for (let i = bits.length - 1; i >= 0; i--) {
        const bit = bits[i];
        const drag = 0.97 ** dt;
        bit.vx *= drag;
        bit.vy = Math.min(bit.vy * drag + bit.gravity * dt, bit.fall);
        bit.tilt += bit.flutter * dt;
        bit.rot += bit.spin * dt;
        bit.x += (bit.vx + Math.sin(bit.tilt) * 1.2) * dt;
        bit.y += bit.vy * dt;
        if (bit.y > H + 30) bits.splice(i, 1);
        else draw(bit);
      }

      if (bits.length || age < 400) requestAnimationFrame(tick);
      else canvas.remove();
    };
    requestAnimationFrame(tick);
  }

  class TwsWpickGifts extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      this.slots = Array.from(this.querySelectorAll("[data-gift-slot]"));
      this.prizes = Array.from(this.querySelector("[data-gift-list]")?.content.children || []);
      this.more = this.querySelector("[data-gifts-more]");
      this.claim = this.querySelector("[data-gifts-claim]");
      this.found = 0;

      this.slots.forEach((slot) => slot.addEventListener("click", () => this.flip(slot)));
      this.more?.addEventListener("click", () => {
        const left = this.slots.filter((slot) => !slot.classList.contains("is-up"));
        this.flip(left[Math.floor(Math.random() * left.length)]);
      });
      this.claim?.addEventListener("click", () => {
        if (this.dataset.claimUrl) {
          window.location.href = this.dataset.claimUrl;
          return;
        }
        this.claimed = true;
        try {
          sessionStorage.setItem("tws-wpick-gifts", "1");
        } catch (e) {}
        document.documentElement.removeAttribute("scroll-lock");
        this.dialog.classList.add("is-leaving");
        setTimeout(
          () => {
            if (this.dialog.open) this.dialog.close();
          },
          reducedMotion() ? 0 : 400,
        );
      });
      // Only the claim button closes it: block Esc, and reopen if the browser
      // forces a close anyway (Chrome does on a repeated Esc).
      this.dialog.addEventListener("cancel", (event) => event.preventDefault());
      this.dialog.addEventListener("close", () => {
        if (!this.claimed && this.dialog.isConnected) this.dialog.showModal();
      });
    }

    flip(slot) {
      const prize = this.prizes[this.found];
      if (!slot || !prize || slot.classList.contains("is-up")) return;
      if (this.hasAttribute("data-sound")) playSound("flip", this.found);
      this.found += 1;
      slot.querySelector("[data-gift-face]").append(prize);
      slot.classList.add("is-up");
      slot.setAttribute("aria-disabled", "true");
      slot.setAttribute("aria-label", prize.textContent.trim());

      const left = this.prizes.length - this.found;
      const count = this.querySelector("[data-gifts-count]");
      count.textContent = this.found;
      count.parentElement.classList.remove("is-bump");
      void count.parentElement.offsetWidth; // restart the bump animation
      count.parentElement.classList.add("is-bump");

      const hint = this.querySelector("[data-gifts-hint]");
      if (hint) hint.hidden = true;
      // Only hand focus to the claim button for keyboard players; on touch it shows a focus ring.
      const keyboard = !!document.activeElement?.matches(":focus-visible");
      this.more.hidden = left === 0;
      this.more.textContent = left === 1 ? this.dataset.moreOne : (this.dataset.more || "").replace("[n]", left);
      // Let the last card finish turning before the celebration.
      if (left === 0) setTimeout(() => this.finish(keyboard), reducedMotion() ? 0 : 600);
    }

    finish(keyboard) {
      this.classList.add("is-done");
      this.querySelector("[data-gifts-heading]").textContent = this.dataset.done;
      const sub = this.querySelector("[data-gifts-sub]");
      if (sub) sub.hidden = true;
      this.slots.forEach((slot) => {
        if (!slot.classList.contains("is-up")) slot.disabled = true;
      });
      this.claim.hidden = false;
      if (keyboard) this.claim.focus({ preventScroll: true });

      if (this.hasAttribute("data-sound")) playSound("win");
      const card = this.querySelector(".tws-wpick-gifts__card").getBoundingClientRect();
      burstConfetti(this.dialog, { x: card.left + card.width / 2, y: card.top + card.height / 2 });
    }
  }

  customElements.define("tws-wpick-hero", TwsWpickHero);
  customElements.define("tws-wpick-sticky", TwsWpickSticky);
  customElements.define("tws-wpick-winners", TwsWpickWinners);
  customElements.define("tws-wpick-gifts", TwsWpickGifts);
})();
